import React from "react";
import { Clock } from "lucide-react";
import { Button, TrashIcon } from "../../../components/ui";
import { DAY_FULL, DAY_SHORT } from "../data/supplementOptions";
import { doseLabel } from "../supplementMapping";
import type { SupplementDose, SupplementDraft } from "../types/supplements.types";

/**
 * One dose as a chip: when it is taken, against which meal, and at what time.
 *
 * "08:00 AM" rather than the list's own "08:00 am" — the chip is read at a
 * glance among several, and the loud half of the label is the one that tells
 * morning from evening.
 */
const DoseChip: React.FC<{ dose: SupplementDose }> = ({ dose }) => (
  <span className="inline-flex items-center gap-2 rounded-[6px] border border-[#88E2ED] bg-white px-3 py-2 text-[13px] 3xl:text-[14px] text-ink">
    <Clock aria-hidden="true" className="h-4 w-4 shrink-0 text-brand" />
    <span>
      {dose.when} {dose.meal}
    </span>
    <span aria-hidden="true" className="text-ink-subtle">
      ·
    </span>
    <span>{dose.time.toUpperCase()}</span>
  </span>
);

export interface SupplementSummaryProps {
  draft: SupplementDraft;
  /** Which supplement of the sitting this is — for naming its controls. */
  index: number;
  /**
   * Hands the card back to the form that writes it. Unset on a plan that is
   * only to be read — one the client already has — and the whole footer goes
   * with it: a card that cannot be modified has nothing to remove either.
   */
  onModify?: () => void;
  /** Unset on the last card standing, the same way the form's bin is. */
  onRemove?: () => void;
}

/**
 * A saved supplement as it is read: what it is and how much of it, the days it
 * is taken on, and the doses as chips.
 *
 * This is what a card becomes once it has been saved, and what opening an
 * existing supplement opens on. A screen of dropdowns is a poor way to read a
 * schedule — nothing on it can be scanned, and every box invites a change
 * nobody meant to make. Modify swaps it for `SupplementCard`, which is the
 * same answers with controls in them.
 *
 * Drawn straight onto the card rather than inside the tinted panel the form
 * uses: the panel is what holds a grid of white controls together, and there
 * are no controls here to hold.
 *
 * The same arrangement the meal plan builder makes of an option — see
 * `MealOptionSummary`, which this is deliberately shaped after so the two
 * halves of a plan read alike.
 */
const SupplementSummary: React.FC<SupplementSummaryProps> = ({
  draft,
  index,
  onModify,
  onRemove,
}) => {
  const owner = draft.name.trim() || `supplement ${index + 1}`;

  // Only the days it is actually taken on, in order. Every day ticked lists
  // all seven, which is what "daily" looks like when it is drawn rather than
  // written — see `frequencyLabel` for where it is written instead.
  const days = [...draft.days].sort((a, b) => a - b);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h3 className="break-words text-[16px] 3xl:text-[18px] font-medium text-ink">
            {draft.name.trim()}
          </h3>
          {/* What one dose is: the strength as typed, in the unit it was
              picked in — "5 mg". See `doseLabel`. */}
          <p className="mt-1 text-[14px] 3xl:text-[15px] text-ink-muted">
            {doseLabel(draft)}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-[11px] 3xl:text-[12px] font-medium uppercase tracking-wide text-ink-muted">
            Frequency:
          </span>
          <ul className="flex flex-wrap gap-2">
            {days.map((day) => (
              <li
                key={DAY_FULL[day]}
                // Filled rather than outlined, and only the chosen days drawn:
                // this is an answer being read back, not seven controls with
                // four of them off.
                className="inline-flex h-8 min-w-[32px] items-center justify-center rounded-[6px] bg-[#00424B] px-2 text-[12px] 3xl:text-[13px] font-medium text-white"
              >
                {/* "Mo" is not a day read aloud, so the whole name is there
                    for a screen reader and the short one for the eye. */}
                <span className="sr-only">{DAY_FULL[day]}</span>
                <span aria-hidden="true">{DAY_SHORT[day]}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* A rule between what the supplement is and when it is taken — the two
          halves of the card, and the only place a reader's eye has to stop. */}
      <p className="mt-4 border-t border-line pt-4 text-[13px] 3xl:text-[14px] text-ink-muted">
        Time per day
      </p>

      <ul className="mt-2 flex flex-wrap gap-3">
        {draft.doses.map((dose) => (
          <li key={dose.id}>
            <DoseChip dose={dose} />
          </li>
        ))}
      </ul>

      {/* Tinted rather than set as another line of text: the schedule above
          says when, and this is the thing that would otherwise be missed —
          how to take it, or what not to take it with. Only drawn where there
          is one. */}
      {draft.note.trim() && (
        <p className="mt-4 rounded-[6px] border border-[#FFDDAF] bg-[#FFF5E2] px-3 py-2.5 text-[13px] 3xl:text-[14px] text-[#C78435]">
          <span className="font-medium">Note:</span> {draft.note.trim()}
        </p>
      )}

      {onModify && (
        <div className="mt-5 flex items-center gap-4 border-t border-line pt-4">
          <Button variant="outline" size="md" onClick={onModify}>
            Modify
          </Button>

          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove ${owner}`}
              className="rounded p-1 text-[#FF3B30] transition-colors hover:bg-[#FDECEC] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SupplementSummary;
